
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useMode } from "@/hooks/useMode";
import { usePerformanceData } from "@/hooks/usePerformanceData";
import { useActiveProject } from "@/hooks/useActiveProject";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, ResponsiveContainer } from "recharts";
import { Users, HelpCircle, TrendingUp, Flag, Sparkles, Package, ArrowRight } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useSkillScores } from "@/hooks/useSkillScores";
import { useSegmentsPerformance } from "@/hooks/useSegmentsPerformance";
import { useCampaignsPerformance } from "@/hooks/useCampaignsPerformance";
import { useHierarchicalCallReasons } from "@/hooks/useHierarchicalCallReasons";
import { DateFilters } from "@/components/DateFilters";
import { PageHeader } from "@/components/PageHeader";
import { ReviewTopics } from "@/components/overview/ReviewTopics";
import { useMonthlyTrends } from "@/hooks/useMonthlyTrends";
import { useProductPerformance } from "@/hooks/useProductPerformance";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { callsPath } from "@/lib/callsUrlState";
import { formatCount, formatDurationString, formatPercent, formatScore } from "@/lib/format";
import { getIndicator, indicatorLabel } from "@/lib/indicatorDefinitions";
import { SERIES_COLORS } from "@/lib/seriesColors";
import { scoreTone } from "@/components/calls/CallStatus";
import { cn } from "@/lib/utils";
import { 
  Tooltip as ShadcnTooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from "@/components/ui/tooltip";

const SegmentPerformanceTable = lazy(() =>
  import("@/components/SegmentPerformanceTable").then((module) => ({
    default: module.SegmentPerformanceTable,
  }))
);
const CampaignPerformanceTable = lazy(() =>
  import("@/components/CampaignPerformanceTable").then((module) => ({
    default: module.CampaignPerformanceTable,
  }))
);
const HierarchicalCallReasons = lazy(() =>
  import("@/components/HierarchicalCallReasons").then((module) => ({
    default: module.HierarchicalCallReasons,
  }))
);
const ProductPerformanceTable = lazy(() =>
  import("@/components/ProductPerformanceTable").then((module) => ({
    default: module.ProductPerformanceTable,
  }))
);
const CustomPerformanceTable = lazy(() =>
  import("@/components/CustomPerformanceTable").then((module) => ({
    default: module.CustomPerformanceTable,
  }))
);

type DashboardTab = "performance" | "raisons" | "clients" | "produits" | "campagnes" | "custom";

/* ─── Séries de tendance : couleurs stables (src/lib/seriesColors.ts) ─── */
const CHART_LEGEND_ITEMS = (isSalesMode: boolean, hasProcedure: boolean) => [
  { key: isSalesMode ? "conversion_rate" : "resolution_rate", label: isSalesMode ? "Taux de conversion" : indicatorLabel("resolution_status"), unit: "%" },
  { key: "satisfaction_rate", label: indicatorLabel("satisfaction_rate"), unit: "%" },
  { key: "performance_rate", label: indicatorLabel("performance"), unit: "/100" },
  { key: "reiteration_rate", label: indicatorLabel("reiteration"), unit: "%" },
  ...(hasProcedure ? [{ key: "procedure_rate", label: indicatorLabel("procedure_score"), unit: "/100" }] : []),
].map((item) => ({ ...item, color: SERIES_COLORS[item.key as keyof typeof SERIES_COLORS] }));

/** Deux ou trois courbes lisibles par défaut ; les autres restent activables. */
const DEFAULT_HIDDEN_SERIES = ["reiteration_rate", "procedure_rate"];

const formatPeriodDate = (date: Date) =>
  date.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

const Dashboard = () => {
  const navigate = useNavigate();

  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [activeTab, setActiveTab] = useState<DashboardTab>("performance");
  const [shouldPreloadTabData, setShouldPreloadTabData] = useState(false);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(() => new Set(DEFAULT_HIDDEN_SERIES));

  const { mode } = useMode();
  const { activeProjectId, isInitialized } = useActiveProject();

  const isSalesMode = mode === "sales";

  const { data: performanceData, isLoading: isLoadingPerformance, isError: isPerformanceError } = usePerformanceData({
    mode,
    projectId: activeProjectId,
    startDate,
    endDate,
    includeAgentMetrics: false,
    includeStagePerformance: false,
  });
  const { data: skillScores } = useSkillScores({ projectId: activeProjectId, startDate, endDate });
  const { data: segmentsPerformance, isLoading: isLoadingSegmentsPerf } = useSegmentsPerformance({
    projectId: activeProjectId,
    startDate,
    endDate,
    enabled: activeTab === "clients" || shouldPreloadTabData,
  });
  const { data: campaignsPerformance, isLoading: isLoadingCampaignsPerf } = useCampaignsPerformance({
    projectId: activeProjectId,
    startDate,
    endDate,
    enabled: activeTab === "campagnes" || shouldPreloadTabData,
  });
  // Chargés d'emblée : les motifs alimentent aussi le bloc « À examiner ».
  const { data: hierarchicalCallReasonsData, isLoading: isLoadingHierarchicalReasons } = useHierarchicalCallReasons({
    projectId: activeProjectId,
    startDate,
    endDate,
  });
  const { data: productsPerformance, isLoading: isLoadingProductsPerf } = useProductPerformance({
    projectId: activeProjectId,
    startDate,
    endDate,
    enabled: activeTab === "produits" || shouldPreloadTabData,
  });

  const { data: monthlyTrends, isLoading: isLoadingMonthlyTrends, isError: isMonthlyTrendsError } = useMonthlyTrends({
    projectId: activeProjectId,
    months: 6,
    startDate,
    endDate
  });

  const isLoading = !isInitialized || isLoadingPerformance || isLoadingMonthlyTrends;
  const startDateKey = startDate?.toISOString() ?? null;
  const endDateKey = endDate?.toISOString() ?? null;

  const hasProcedure = (skillScores?.procedure ?? 0) > 0;
  const legendItems = useMemo(() => CHART_LEGEND_ITEMS(isSalesMode, hasProcedure), [isSalesMode, hasProcedure]);

  const toggleSeries = (key: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    setShouldPreloadTabData(false);
  }, [activeProjectId, mode, startDateKey, endDateKey]);

  useEffect(() => {
    if (
      shouldPreloadTabData ||
      !isInitialized ||
      !activeProjectId ||
      isLoadingPerformance ||
      isLoadingMonthlyTrends
    ) {
      return;
    }

    let idleCallbackId: any = null;
    let timeoutId: any = null;

    const preloadTabs = () => {
      setShouldPreloadTabData(true);
      void import("@/components/SegmentPerformanceTable");
      void import("@/components/CampaignPerformanceTable");
      void import("@/components/HierarchicalCallReasons");
      void import("@/components/ProductPerformanceTable");
      void import("@/components/CustomPerformanceTable");
    };

    if (typeof window !== 'undefined') {
      if ("requestIdleCallback" in window) {
        idleCallbackId = (window as any).requestIdleCallback(preloadTabs, { timeout: 1500 });
      } else {
        timeoutId = (window as any).setTimeout(preloadTabs, 300);
      }
    }

    return () => {
      if (idleCallbackId !== null && typeof window !== 'undefined' && "cancelIdleCallback" in window) {
        (window as any).cancelIdleCallback(idleCallbackId);
      }
      if (timeoutId !== null && typeof window !== 'undefined') {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    activeProjectId,
    isInitialized,
    isLoadingMonthlyTrends,
    isLoadingPerformance,
    shouldPreloadTabData,
  ]);


  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true" aria-label="Chargement de la vue d'ensemble">
        <div className="h-8 w-64 rounded bg-muted animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
        <div className="h-40 rounded-xl bg-muted animate-pulse" />
        <div className="h-80 rounded-xl bg-muted animate-pulse" />
      </div>
    );
  }

  if (!activeProjectId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-lg text-center space-y-4">
          <h2 className="text-2xl font-bold">Aucun projet actif</h2>
          <p className="text-muted-foreground">
            Votre projet est en cours d'initialisation. Ouvrez la liste des appels pour vérifier les imports.
          </p>
          <Button onClick={() => navigate("/appels")}>Voir les appels importés</Button>
        </div>
      </div>
    );
  }

  if (isPerformanceError || isMonthlyTrendsError || !performanceData) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="max-w-lg text-center space-y-4">
          <h2 className="text-2xl font-bold">Projet créé, données en chargement</h2>
          <p className="text-muted-foreground">
            Les métriques du dashboard sont en cours de préparation. Vous pouvez déjà vérifier vos appels importés.
          </p>
          <Button onClick={() => navigate("/appels")}>Voir les appels importés</Button>
        </div>
      </div>
    );
  }

  const performanceBySkill = [
    { key: "accueil_score", value: skillScores?.accueil },
    { key: "empathie_score", value: skillScores?.empathie },
    { key: "resolution_score", value: skillScores?.resolution },
    { key: "conclusion_score", value: skillScores?.conclusion },
    ...(hasProcedure ? [{ key: "procedure_score", value: skillScores?.procedure }] : []),
  ];

  const metrics = performanceData.globalMetrics;
  const hasCalls = (metrics.total_calls ?? 0) > 0;
  // La liste d'appels reprend la période de la vue d'ensemble : les chiffres
  // ne se contredisent pas d'un écran à l'autre.
  const periodRange = { from: startDate?.toISOString(), to: endDate?.toISOString() };
  const periodLabel = startDate && endDate
    ? `du ${formatPeriodDate(startDate)} au ${formatPeriodDate(endDate)}`
    : startDate ? `depuis le ${formatPeriodDate(startDate)}` : endDate ? `jusqu'au ${formatPeriodDate(endDate)}` : "toute la période";

  // §4 — quatre indicateurs majeurs : valeur, unité, périmètre et accès au détail.
  // Pas de comparaison affichée tant que la période précédente n'est pas
  // calculée (les valeurs « previous_* » du hook sont initialisées à zéro).
  const majorMetrics = [
    isSalesMode
      ? {
        key: "conversion_rate",
        label: "Taux de conversion",
        help: "Part des appels ayant abouti à une vente.",
        value: formatPercent(metrics.conversion_rate),
        link: callsPath({ dateRange: periodRange, convertedFilter: "false" }),
        linkLabel: "Voir les appels non convertis",
      }
      : {
        key: "resolution_status",
        label: indicatorLabel("resolution_status"),
        help: getIndicator("resolution_status")?.short,
        value: formatPercent(metrics.resolution_rate),
        link: callsPath({ dateRange: periodRange, resolutionFilter: ["Non résolu", "Résolu partiellement"] }),
        linkLabel: "Voir les appels non résolus",
      },
    {
      key: "satisfaction_rate",
      label: indicatorLabel("satisfaction_rate"),
      help: getIndicator("satisfaction_rate")?.short,
      value: formatPercent(metrics.satisfaction_rate),
      link: callsPath({ dateRange: periodRange, sortColumn: "satisfaction", sortDirection: "asc" }),
      linkLabel: "Voir les moins satisfaits d'abord",
    },
    {
      key: "reiteration",
      label: indicatorLabel("reiteration"),
      help: getIndicator("reiteration")?.short,
      value: formatPercent(metrics.reiteration_rate),
      link: callsPath({ dateRange: periodRange, reiterationFilter: ["Deuxième itération", "Troisième itération et plus"] }),
      linkLabel: "Voir les réitérations",
    },
    {
      key: "performance",
      label: indicatorLabel("performance"),
      help: getIndicator("performance")?.short,
      value: formatScore(metrics.performance_rate),
      link: callsPath({ dateRange: periodRange, sortColumn: "performance", sortDirection: "asc" }),
      linkLabel: "Voir les notes les plus basses",
    },
  ];

  const tooltipStyle = {
    backgroundColor: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: '8px',
    boxShadow: 'var(--overlay-shadow)',
    padding: '8px 12px',
    fontSize: '13px',
  };

  return (
    <TooltipProvider delayDuration={300}>
      <PageHeader
        title="Vue d'ensemble"
        description={
          <span className="tabular-nums">
            {hasCalls ? `${formatCount(metrics.total_calls)} appels` : "Aucun appel"} · {periodLabel}
          </span>
        }
      >
        <DateFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onReset={() => {
            setStartDate(null);
            setEndDate(null);
          }}
          compact={true}
        />
      </PageHeader>

      {/* ─── Indicateurs majeurs ─── */}
      <section aria-label="Indicateurs majeurs" className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {majorMetrics.map((metric) => (
          <article key={metric.key} className="flex flex-col rounded-xl border border-border bg-card p-4">
            <ShadcnTooltip>
              <TooltipTrigger asChild>
                <h2 className="w-fit cursor-help text-sm font-medium text-muted-foreground">{metric.label}</h2>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[260px] text-xs leading-relaxed">{metric.help}</TooltipContent>
            </ShadcnTooltip>
            <p className="mt-1 font-display text-[2rem] font-semibold leading-none text-foreground">
              {hasCalls ? metric.value : <span className="text-base font-medium text-muted-foreground">Données insuffisantes</span>}
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground tabular-nums">
              {hasCalls ? `sur ${formatCount(metrics.total_calls)} appels` : periodLabel}
            </p>
            {hasCalls && (
              <Link
                to={metric.link}
                className="mt-auto inline-flex items-center gap-1 pt-3 text-sm font-medium text-primary hover:underline underline-offset-4"
              >
                {metric.linkLabel}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            )}
          </article>
        ))}
      </section>

      {/* ─── Contexte : volume et durée, ni progrès ni dégradation en soi ─── */}
      <dl className="mb-4 flex flex-wrap gap-x-8 gap-y-2 rounded-xl border border-border bg-card px-4 py-3 text-sm">
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground">{indicatorLabel("total_calls")}</dt>
          <dd className="font-semibold tabular-nums">{formatCount(metrics.total_calls)}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground">{indicatorLabel("average_duration")}</dt>
          <dd className="font-semibold tabular-nums">{hasCalls ? formatDurationString(metrics.average_duration) : "—"}</dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-muted-foreground">Comparaison à la période précédente</dt>
          <dd className="text-muted-foreground">pas encore disponible</dd>
        </div>
      </dl>

      {/* ─── Points à examiner ─── */}
      <div className="mb-6">
        <ReviewTopics
          data={hierarchicalCallReasonsData}
          isLoading={isLoadingHierarchicalReasons}
          isSalesMode={isSalesMode}
          globalResolutionRate={metrics.resolution_rate ?? 0}
          globalReiterationRate={metrics.reiteration_rate ?? 0}
          totalCalls={metrics.total_calls ?? 0}
          dateRange={periodRange}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="performance" value={activeTab} onValueChange={(v) => setActiveTab(v as DashboardTab)} className="w-full space-y-4">
        <TabsList className="max-w-full overflow-x-auto no-scrollbar justify-start">
          {([
            { value: "performance", icon: TrendingUp, label: "Tendances" },
            { value: "raisons", icon: HelpCircle, label: "Motifs d'appel" },
            { value: "clients", icon: Users, label: "Segments" },
            { value: "produits", icon: Package, label: "Produits" },
            { value: "campagnes", icon: Flag, label: "Campagnes" },
            { value: "custom", icon: Sparkles, label: "Analyses personnalisées" },
          ] as const).map(({ value, icon: Icon, label }) => (
            <TabsTrigger key={value} value={value} className="gap-2">
              <Icon className="h-4 w-4 hidden sm:inline" aria-hidden="true" />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Contenu Tab Tendances */}
        <TabsContent value="performance" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Évolution mensuelle — 2/3 */}
            <section className="lg:col-span-2 rounded-xl border border-border bg-card p-4" aria-labelledby="evolution-mensuelle">
              <h2 id="evolution-mensuelle" className="text-base font-semibold mb-3">Évolution mensuelle</h2>

              {/* Sélecteur de séries : légende cliquable, état annoncé */}
              <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Séries affichées">
                {legendItems.map((item) => {
                  const shown = !hiddenSeries.has(item.key);
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => toggleSeries(item.key)}
                      aria-pressed={shown}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm transition-colors",
                        shown ? "border-border bg-card text-foreground" : "border-dashed border-border text-muted-foreground"
                      )}
                    >
                      <span
                        className="h-0.5 w-4 rounded-full"
                        style={{ backgroundColor: shown ? item.color : "hsl(var(--muted-foreground))" }}
                        aria-hidden="true"
                      />
                      {item.label}
                    </button>
                  );
                })}
              </div>

              <div className="w-full min-w-0 flex flex-col">
                <div className="h-56 w-full">
                  {monthlyTrends && monthlyTrends.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={monthlyTrends}
                        syncId="monthlyPerformanceSync"
                        margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                      >
                        <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" hide={true} />
                        <YAxis
                          domain={[0, 100]}
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                          width={36}
                        />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          itemStyle={{ color: 'hsl(var(--foreground))' }}
                          labelStyle={{ color: 'hsl(var(--muted-foreground))', marginBottom: 4 }}
                          formatter={(value: number | undefined, name: string | undefined) => {
                            const item = legendItems.find((l) => l.label === name);
                            return [value === undefined || value === null ? "—" : item?.unit === "/100" ? formatScore(value) : formatPercent(value), name ?? ""];
                          }}
                        />
                        {legendItems.filter((item) => !hiddenSeries.has(item.key)).map((item) => (
                          <Line
                            key={item.key}
                            type="monotone"
                            dataKey={item.key}
                            name={item.label}
                            stroke={item.color}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4, fill: item.color, strokeWidth: 2, stroke: 'hsl(var(--card))' }}
                            connectNulls
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <p className="text-muted-foreground text-sm">{isLoadingMonthlyTrends ? "Chargement…" : "Données insuffisantes pour tracer une évolution."}</p>
                    </div>
                  )}
                </div>

                {/* Volume mensuel, contexte de lecture des courbes */}
                <div className="h-24 w-full">
                  {monthlyTrends && monthlyTrends.length > 0 && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={monthlyTrends}
                        syncId="monthlyPerformanceSync"
                        margin={{ top: 4, right: 12, left: 0, bottom: 0 }}
                      >
                        <XAxis
                          dataKey="month"
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        />
                        <YAxis
                          axisLine={false}
                          tickLine={false}
                          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                          width={36}
                        />
                        <Tooltip
                          cursor={{ fill: 'hsl(var(--muted))' }}
                          contentStyle={tooltipStyle}
                          formatter={(value: number | undefined) => [`${formatCount(value ?? 0)} appels`, indicatorLabel("total_calls")]}
                        />
                        <Bar
                          dataKey="call_count"
                          name={indicatorLabel("total_calls")}
                          fill={SERIES_COLORS.call_count}
                          opacity={0.35}
                          radius={[3, 3, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  Un mois en cours ou incomplet repose sur moins d'appels : comparez-le avec prudence (volume ci-dessus).
                </p>
              </div>
            </section>

            {/* Compétences : barres horizontales, plus faciles à comparer que des jauges */}
            <section className="rounded-xl border border-border bg-card p-4" aria-labelledby="competences">
              <h2 id="competences" className="text-base font-semibold mb-4">
                {isSalesMode ? "Compétences de vente" : "Compétences relationnelles"}
              </h2>
              <ul className="space-y-4">
                {performanceBySkill.map((skill) => {
                  const v = typeof skill.value === "number" && skill.value > 0 ? Math.round(skill.value) : null;
                  const tone = scoreTone(v);
                  return (
                    <li key={skill.key} title={getIndicator(skill.key)?.short}>
                      <div className="mb-1 flex items-baseline justify-between gap-2 text-sm">
                        <span className="text-foreground">{indicatorLabel(skill.key)}</span>
                        <span className={cn("font-medium tabular-nums", v === null && "text-muted-foreground font-normal")}>
                          {v === null ? "Non évalué" : formatScore(v)}
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
                        <div
                          className={cn("h-full rounded-full", tone === "success" ? "bg-success" : tone === "warning" ? "bg-warning" : "bg-destructive")}
                          style={{ width: `${v ?? 0}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          </div>
        </TabsContent>

        {/* Contenu Tab Raisons d'appel */}
        <TabsContent value="raisons" className="mt-0 w-full max-w-full min-w-0 overflow-hidden outline-none">
          {isLoadingHierarchicalReasons ? (
            <div className="flex items-center justify-center h-96 rounded-xl border border-border bg-card">
              <div className="text-center">
                <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_2s_linear_infinite]" />
                <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
              </div>
            </div>
          ) : hierarchicalCallReasonsData && hierarchicalCallReasonsData.length > 0 ? (
            <Suspense fallback={<div className="h-40 skeleton-card" />}>
              <div className="w-full overflow-x-auto pb-4 custom-scrollbar">
                <HierarchicalCallReasons
                  data={hierarchicalCallReasonsData}
                  isSalesMode={isSalesMode}
                  projectId={Number(activeProjectId)}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </Suspense>
          ) : (
            <div className="rounded-xl border border-border bg-card p-8 sm:p-12 md:p-20 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-muted/40 flex items-center justify-center mb-6">
                <HelpCircle className="h-8 w-8 text-muted-foreground/30" />
              </div>
              <p className="text-muted-foreground font-medium">Aucune donnée disponible pour les motifs d'appel</p>
            </div>
          )}
        </TabsContent>

        {/* Contenu Tab Clients/Segments */}
        <TabsContent value="clients" className="mt-0 min-h-[400px] w-full max-w-full min-w-0 overflow-hidden outline-none">
          {isLoadingSegmentsPerf ? (
            <div className="flex items-center justify-center h-96 rounded-xl border border-border bg-card">
              <div className="text-center">
                <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_2s_linear_infinite]" />
                <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
              </div>
            </div>
          ) : segmentsPerformance && segmentsPerformance.length > 0 ? (
            <Suspense fallback={<div className="h-40 skeleton-card" />}>
              <div className="w-full overflow-x-auto pb-4 custom-scrollbar">
                <SegmentPerformanceTable
                  data={segmentsPerformance}
                  isSalesMode={isSalesMode}
                  projectId={Number(activeProjectId)}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </Suspense>
          ) : (
            <div className="rounded-xl border border-border bg-card p-8 sm:p-12 md:p-20 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-muted/40 flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-muted-foreground/30" />
              </div>
              <p className="text-muted-foreground font-medium">Aucune donnée disponible pour les segments</p>
            </div>
          )}
        </TabsContent>

        {/* Contenu Tab Produits */}
        <TabsContent value="produits" className="mt-0 min-h-[400px] w-full max-w-full min-w-0 overflow-hidden outline-none">
          {isLoadingProductsPerf ? (
            <div className="flex items-center justify-center h-96 rounded-xl border border-border bg-card">
              <div className="text-center">
                <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_2s_linear_infinite]" />
                <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
              </div>
            </div>
          ) : productsPerformance && productsPerformance.length > 0 ? (
            <Suspense fallback={<div className="h-40 skeleton-card" />}>
              <div className="w-full overflow-x-auto pb-4 custom-scrollbar">
                <ProductPerformanceTable
                  data={productsPerformance}
                  isSalesMode={isSalesMode}
                  projectId={Number(activeProjectId)}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </Suspense>
          ) : (
            <div className="rounded-xl border border-border bg-card p-8 sm:p-12 md:p-20 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-muted/40 flex items-center justify-center mb-6">
                <Package className="h-8 w-8 text-muted-foreground/30" />
              </div>
              <p className="text-muted-foreground font-medium">Aucune donnée disponible pour les produits</p>
            </div>
          )}
        </TabsContent>

        {/* Contenu Tab Campagnes */}
        <TabsContent value="campagnes" className="mt-0 min-h-[400px] w-full max-w-full min-w-0 overflow-hidden outline-none">
          {isLoadingCampaignsPerf ? (
            <div className="flex items-center justify-center h-96 rounded-xl border border-border bg-card">
              <div className="text-center">
                <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_2s_linear_infinite]" />
                <p className="mt-4 text-sm text-muted-foreground">Chargement…</p>
              </div>
            </div>
          ) : campaignsPerformance && campaignsPerformance.length > 0 ? (
            <Suspense fallback={<div className="h-40 skeleton-card" />}>
              <div className="w-full overflow-x-auto pb-4 custom-scrollbar">
                <CampaignPerformanceTable
                  data={campaignsPerformance}
                  isSalesMode={isSalesMode}
                  projectId={Number(activeProjectId)}
                  startDate={startDate}
                  endDate={endDate}
                />
              </div>
            </Suspense>
          ) : (
            <div className="rounded-xl border border-border bg-card p-8 sm:p-12 md:p-20 flex flex-col items-center justify-center text-center">
              <div className="w-16 h-16 rounded-full bg-muted/40 flex items-center justify-center mb-6">
                <Flag className="h-8 w-8 text-muted-foreground/30" />
              </div>
              <p className="text-muted-foreground font-medium">Aucune donnée disponible pour les campagnes</p>
            </div>
          )}
        </TabsContent>

        {/* Contenu Tab Analytique Personnalisée */}
        <TabsContent value="custom" className="mt-0 w-full max-w-full min-w-0 overflow-hidden outline-none">
          <Suspense fallback={<div className="h-96 rounded-xl border border-border bg-card flex items-center justify-center animate-pulse text-muted-foreground">Préparation de l'espace analytique...</div>}>
            <CustomPerformanceTable
              isSalesMode={isSalesMode}
              projectId={Number(activeProjectId)}
              startDate={startDate}
              endDate={endDate}
            />
          </Suspense>
        </TabsContent>
      </Tabs>
    </TooltipProvider>
  );
};

export default Dashboard;
